import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

/**
 * StatusCodeChart - Donut chart showing HTTP status code distribution
 */
const StatusCodeChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-40 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Status Code Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No status data available
        </div>
      </div>
    );
  }

  // Extract distribution from API response (API returns { distribution: {...}, details: {...} })
  const distribution = data.distribution || data;

  // Prepare chart data
  const chartData = [
    { name: '2xx Success', value: distribution['2xx'] || 0, color: '#22c55e' },
    { name: '3xx Redirect', value: distribution['3xx'] || 0, color: '#3b82f6' },
    { name: '4xx Client Error', value: distribution['4xx'] || 0, color: '#f59e0b' },
    { name: '5xx Server Error', value: distribution['5xx'] || 0, color: '#ef4444' }
  ].filter(item => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Status Code Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No requests recorded
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4">Status Code Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [
                `${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`,
                'Requests'
              ]}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value, entry) => {
                const item = chartData.find(d => d.name === value);
                if (item) {
                  return `${value}: ${item.value.toLocaleString()}`;
                }
                return value;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Status code breakdown */}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        <StatusBadge label="2xx" value={distribution['2xx'] || 0} color="green" total={total} />
        <StatusBadge label="3xx" value={distribution['3xx'] || 0} color="blue" total={total} />
        <StatusBadge label="4xx" value={distribution['4xx'] || 0} color="yellow" total={total} />
        <StatusBadge label="5xx" value={distribution['5xx'] || 0} color="red" total={total} />
      </div>
    </div>
  );
};

/**
 * Status badge component
 */
const StatusBadge = ({ label, value, color, total }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    red: 'bg-red-100 text-red-700'
  };

  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  return (
    <div className={`rounded px-2 py-1 ${colorClasses[color]}`}>
      <div className="text-xs font-medium">{label}</div>
      <div className="text-sm font-bold">{percentage}%</div>
    </div>
  );
};

export default StatusCodeChart;
